// @givmo/employer-module — the words of the two acts that choose what a person can open (EP-SUITE8):
// approving a join request (both hosts) and the portal Admins page's role edit. ONE table, so the
// words Jonathan ruled on are the words both screens draw (D-EP-76, 2026-10-24: "approved").
//
// Every sentence the console's own composer already says for the same decision is the console's.
// Where `@givmo/org-console` exports the ruled words they are IMPORTED, never re-typed: the Global
// Admin card's name and line (`GLOBAL_ADMIN_LABEL`, `globalAdminDescription`), the line under "Which
// apps they can open" (`TEAM_COPY.exactlyWhatYouChoose`, D-CPO-77) and the off badge
// (`offForOrgLabel`). The rest its invite composer and access editor spell inline, so they are taken
// as they stand: the Global Admin notice, "Which apps they can open", "Level", the loading and empty
// lines, and "Choose at least one app, or make them a Global Admin."
//
// ⚠ THE ROLE EDIT'S LINES ARE THE CONSOLE'S, IMPORTED AND NEVER RE-TYPED: `DEMOTION_COPY`, the words
// Jonathan approved on the console's demotion screen (D-CPO-67), pinned by org-console's copy law
// (D-EP-76 (3)-(4): a portal variant would be a new ruling).
//
// An approval's refusal says first that nothing was approved, because the door checks the whole
// choice before it writes anything, and the person must never read a refusal as a half-made decision.

import {
  DEMOTION_COPY,
  GLOBAL_ADMIN_LABEL,
  TEAM_COPY,
  globalAdminDescription,
  offForOrgLabel,
} from '@givmo/org-console';
import { productLabel } from '../../lib/app-names';
import type { ChoiceRefusal, RequestedAppState } from './app-choice';

/** The words both approve screens and the role edit's picker share. */
export const APP_CHOICE_WORDS = {
  globalAdmin: GLOBAL_ADMIN_LABEL,
  globalAdminDescription: globalAdminDescription('company'),
  globalAdminNotice: `A ${GLOBAL_ADMIN_LABEL} has every app, at Owner level. Turn ${GLOBAL_ADMIN_LABEL} off to choose app by app.`,
  appsHeading: 'Which apps they can open',
  exactly: TEAM_COPY.exactlyWhatYouChoose,
  level: 'Level',
  loading: 'Loading your apps…',
  empty: 'This company has no apps to offer yet.',
  loadFailed: 'Your apps couldn’t be loaded.',
  retry: 'Try again',
  /** An app the company has switched off: still listed and still choosable (D-EP-76 (2)). */
  offForCompany: offForOrgLabel('company'),
} as const;

/** The approve screen's own words. */
export const APPROVE_WORDS = {
  lead: (who: string) => `Approving adds ${who} to your company.`,
  /** The inbox column naming what each requester asked for (was "Product"). */
  askedForColumn: 'Asked for',
} as const;

/**
 * The requester's note, the lead's second sentence: what they asked for (the app's name set apart,
 * so the eye finds it), whether the company has it switched off (it can still be chosen, and opens
 * once it is on), or that the company can't give it. `null` when they said nothing.
 */
export function requestedAppNote(
  state: RequestedAppState,
): { lead: string; app: string; tail: string } | null {
  switch (state.kind) {
    case 'none':
      return null;
    case 'on':
      return { lead: 'They asked for ', app: state.label, tail: '.' };
    case 'off':
      return {
        lead: 'They asked for ',
        app: state.label,
        tail: ', which your company has turned off. They can open it once it’s turned on.',
      };
    case 'unavailable':
      return {
        lead: 'They asked for ',
        app: state.label,
        tail: ', which your company can’t give.',
      };
  }
}

/** A refused approval, in words; `null` hands the caller back to the server's own answer. */
export function approveRefusalWords(refusal: ChoiceRefusal | null): string | null {
  if (refusal === null) return null;
  switch (refusal.kind) {
    case 'choice-required':
      return `Nothing was approved. Choose at least one app, or make them a ${GLOBAL_ADMIN_LABEL}.`;
    case 'modules-with-global-admin':
      return `Nothing was approved. A ${GLOBAL_ADMIN_LABEL} already has every app, so choose apps or ${GLOBAL_ADMIN_LABEL}, not both.`;
    case 'module-unavailable':
      return `Nothing was approved. ${refusal.label} isn’t available to your company any more, so choose again.`;
    case 'duplicate-module':
      return `Nothing was approved. ${refusal.label} was chosen twice, so try again.`;
    case 'not-found':
      return 'Nothing was approved. This request can’t be approved right now, so refresh the page and try again.';
    case 'not-global-admin':
    case 'last-global-admin':
      return null;
  }
}

/**
 * A refused role change, in the console's ruled words (`DEMOTION_COPY`), or `null` for every refusal
 * those words don't name — the two codes these controls cannot send (apps beside a Global Admin, an
 * app twice), a backend without the route, anything else — which the editor answers with its one
 * generic failure line, as the console's does. `who` is the person being changed.
 */
export function roleChangeRefusalLine(refusal: ChoiceRefusal | null, who: string): string | null {
  switch (refusal?.kind) {
    case 'last-global-admin':
      return DEMOTION_COPY.onlyGlobalAdmin;
    case 'not-global-admin':
      return DEMOTION_COPY.changedElsewhere(who);
    case 'module-unavailable':
      return DEMOTION_COPY.appWithdrawn(
        refusal.module === null ? null : productLabel(refusal.module),
        'company',
      );
    case 'choice-required':
      return DEMOTION_COPY.choiceRequired;
    default:
      return null;
  }
}
